package info.rezident.app.data.quiz

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.Direction
import dev.gitlive.firebase.firestore.DocumentSnapshot
import dev.gitlive.firebase.firestore.FirebaseFirestore
import dev.gitlive.firebase.firestore.firestore
import dev.gitlive.firebase.firestore.js
import info.rezident.app.core.LocalStorage
import info.rezident.app.core.LocalStorage.KEY_QUIZ_USER_ANSWERS
import info.rezident.app.core.Logger
import info.rezident.app.data.auth.AuthRepository
import info.rezident.app.data.common.AcademicYearResolver
import info.rezident.app.pages.recommendations.RecommendationsFeedback
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.*
import kotlinx.serialization.json.Json
import kotlin.js.Date

data class PaymentSuccessInfo(
    val paymentId: String?,
    val amountMinorUnits: Long?,
    val currencyCode: String?
)

/**
 * Repository for user-specific Quiz data, handling user quiz completions,
 * answers, recommendations, feedback, and unlock state.
 */
object UserQuizRepository {
    private val firestore: FirebaseFirestore = Firebase.firestore

    // Add coroutine scope for background operations that survive navigation
    private val repositoryScope = CoroutineScope(Dispatchers.Default + SupervisorJob())

    // New state flow for user completed quizzes
    private val _userQuizzes = MutableStateFlow<List<CompletedQuiz>>(emptyList())
    val userQuizzes: StateFlow<List<CompletedQuiz>> = _userQuizzes.asStateFlow()

    // The user whose quizzes are in [_userQuizzes]. The cache is only served to that same user, so
    // nothing leaks to another account after logging out or switching accounts in the same tab.
    private var userQuizzesOwnerUid: String? = null

    // Track saving state
    private val _isSavingQuiz = MutableStateFlow(false)
    val isSavingQuiz: StateFlow<Boolean> = _isSavingQuiz.asStateFlow()

    // Track current answers to prevent duplicate saves
    private var currentAnswersHash: String? = null

    // Flow for tracking the saved quiz document ID
    private val _savedQuizDocumentId = MutableStateFlow<String?>(null)
    val savedQuizDocumentId: StateFlow<String?> = _savedQuizDocumentId.asStateFlow()

    // StateFlow for user answers to enable reactive updates
    private val _userAnswers =
        MutableStateFlow(QuizUserAnswers(templateId = CURRENT_TEMPLATE_VERSION, answers = emptyList()))
    val userAnswers: StateFlow<QuizUserAnswers> = _userAnswers.asStateFlow()

    init {
        restoreUserAnswers()
    }

    /**
     * Saves quiz answers in background and updates the document ID flow.
     * Prevents duplicate saves and provides progress tracking.
     */
    private suspend fun saveQuizAnswersInBackground(quizUserAnswers: QuizUserAnswers) {
        val answersHash = quizUserAnswers.hashCode().toString()

        // Check if we're already saving these exact answers
        if (currentAnswersHash == answersHash && _savedQuizDocumentId.value != null) {
            return
        }

        // If already saving, don't start another operation
        if (_isSavingQuiz.value) {
            return
        }

        // Start new save operation
        _isSavingQuiz.value = true

        try {
            val documentId = saveQuizAnswersAndReturnDocumentId(quizUserAnswers)
            if (documentId != null) {
                currentAnswersHash = answersHash
                _savedQuizDocumentId.value = documentId
            }
        } catch (e: Exception) {
            Logger.error("Error saving quiz answers: ${e.message}")
        } finally {
            _isSavingQuiz.value = false
        }
    }

    /**
     * Starts saving in background without waiting (fire and forget with state tracking)
     */
    fun startSavingQuizAnswersInBackground(quizUserAnswers: QuizUserAnswers) {
        val answersHash = quizUserAnswers.hashCode().toString()

        // Don't start if already saving these answers
        if (currentAnswersHash == answersHash || _isSavingQuiz.value) {
            return
        }

        repositoryScope.launch {
            saveQuizAnswersInBackground(quizUserAnswers)
        }
    }

    /**
     * Fetches all completed quizzes for the current user with their answers and recommendations
     */
    suspend fun fetchUserQuizzes(): Boolean = withContext(Dispatchers.Default) {
        try {
            val uid = AuthRepository.getCurrentUser()?.uid ?: return@withContext false
            val quizzesSnapshot = firestore.collection("users").document(uid)
                .collection("quizzes")
                .orderBy("createdAt", Direction.DESCENDING)
                .get()

            // Fetch each quiz's answers concurrently instead of one quiz after another
            val completedQuizzes = coroutineScope {
                quizzesSnapshot.documents.map { quizDoc ->
                    async {
                        val quizData = quizDoc.data<QuizUserAnswersModel>()

                        // Fetch answers for this quiz
                        val answersSnapshot = quizDoc.reference.collection("answers").get()
                        val answers = answersSnapshot.documents.map { answerDoc ->
                            val answerData = answerDoc.data<CompletedAnswerModel>()

                            UserAnswer(
                                questionId = answerData.questionId,
                                answerIds = answerData.answersIds,
                                openAnswer = answerData.openAnswer,
                                fiveScaleAnswer = answerData.fiveScaleAnswer
                            )
                        }

                        // Check for recommendations in this quiz document (cloud function format only)
                        val recommendations = if (quizDoc.contains("recommendations")) {
                            quizDoc.get<RecommendationsModel>("recommendations").toDomainModel()
                        } else null

                        quizData.toDomainModel(
                            documentId = quizDoc.id,
                            answers = answers,
                            recommendations = recommendations
                        )
                    }
                }.awaitAll()
            }

            _userQuizzes.update { completedQuizzes }
            userQuizzesOwnerUid = uid
            true
        } catch (e: Exception) {
            Logger.error("Error fetching user quizzes: ${e.message}")
            false
        }
    }


    /**
     * Gets a quiz document (template, unlock state, recommendations, locked ranks) straight from
     * Firestore, without its answers, which is a single read. [CompletedQuiz.answers] is empty; use
     * [quizHasAnswers] to know if there are any and [getCompletedQuizWithAnswers] to get them.
     *
     * @return the quiz, or null if it doesn't exist under the current user or it couldn't be read
     */
    suspend fun getQuizWithoutAnswers(quizDocumentId: String): CompletedQuiz? =
        withContext(Dispatchers.Default) {
            try {
                val uid = AuthRepository.getCurrentUser()?.uid ?: return@withContext null
                val quizDoc = firestore.collection("users").document(uid)
                    .collection("quizzes").document(quizDocumentId).get()

                if (!quizDoc.exists) {
                    return@withContext null
                }

                quizDoc.toCompletedQuiz(answers = emptyList())
            } catch (e: Exception) {
                Logger.error("Error getting quiz $quizDocumentId: ${e.message}")
                null
            }
        }

    /**
     * Checks if a quiz has any answers, fetching at most one of them
     */
    suspend fun quizHasAnswers(quizDocumentId: String): Boolean =
        withContext(Dispatchers.Default) {
            try {
                val uid = AuthRepository.getCurrentUser()?.uid ?: return@withContext false
                firestore.collection("users").document(uid)
                    .collection("quizzes").document(quizDocumentId)
                    .collection("answers").limit(1).get()
                    .documents.isNotEmpty()
            } catch (e: Exception) {
                Logger.error("Error checking answers of quiz $quizDocumentId: ${e.message}")
                false
            }
        }

    /**
     * Sets recommendations (from Cloud Function) for a specific quiz document
     */
    suspend fun setRecommendationsForQuiz(quizDocumentId: String, result: RecommendationsResult): Boolean =
        withContext(Dispatchers.Default) {
            try {
                val uid = AuthRepository.getCurrentUser()?.uid ?: return@withContext false
                val quizDocRef = firestore.collection("users").document(uid)
                    .collection("quizzes").document(quizDocumentId)

                val recommendationsModel = result.toFirestoreModel(getCurrentDateTime())
                quizDocRef.update("recommendations" to recommendationsModel)

                // Update cached quiz data
                _userQuizzes.update { quizzes ->
                    quizzes.map { quiz ->
                        if (quiz.quizId == quizDocumentId) {
                            quiz.copy(recommendations = result)
                        } else quiz
                    }
                }

                true
            } catch (e: Exception) {
                Logger.error("Error setting recommendations for quiz $quizDocumentId: ${e.message}")
                false
            }
        }

    /**
     * Sets the unlock state flag on a specific quiz document
     */
    suspend fun setQuizUnlocked(quizDocumentId: String, isUnlocked: Boolean): Boolean =
        withContext(Dispatchers.Default) {
            try {
                val uid = AuthRepository.getCurrentUser()?.uid ?: return@withContext false
                val quizDocRef = firestore.collection("users").document(uid)
                    .collection("quizzes").document(quizDocumentId)
                quizDocRef.update("isUnlocked" to isUnlocked)

                // Update cache
                _userQuizzes.update { quizzes ->
                    quizzes.map { q -> if (q.quizId == quizDocumentId) q.copy(isUnlocked = isUnlocked) else q }
                }

                true
            } catch (e: Exception) {
                Logger.error("Error updating quiz unlock state for $quizDocumentId: ${e.message}")
                false
            }
        }

    /**
     * Checks if a quiz document exists for the current user
     */
    suspend fun quizDocumentExistsInCurrentUserDoc(quizDocumentId: String): Boolean =
        withContext(Dispatchers.Default) {
            try {
                val uid = AuthRepository.firebaseUser.firstOrNull()?.uid ?: return@withContext false
                val quizDoc = firestore.collection("users").document(uid)
                    .collection("quizzes").document(quizDocumentId).get()
                quizDoc.exists
            } catch (e: Exception) {
                Logger.error("Error checking if quiz document exists for $quizDocumentId: ${e.message}")
                false
            }
        }

    /**
     * Save feedback for a specific quiz document in Firestore
     * Stores feedback in the feedback field of the quiz document using direct serialization
     */
    suspend fun saveFeedbackForQuiz(
        quizDocumentId: String,
        feedback: RecommendationsFeedback
    ): Boolean =
        withContext(Dispatchers.Default) {
            try {
                val uid = AuthRepository.getCurrentUser()?.uid ?: return@withContext false
                val quizDocRef = firestore.collection("users").document(uid)
                    .collection("quizzes").document(quizDocumentId)

                // Use direct serialization instead of manual map creation
                quizDocRef.set(mapOf("feedback" to feedback), merge = true)

                true
            } catch (e: Exception) {
                Logger.error("Error saving feedback for quiz $quizDocumentId: ${e.message}")
                false
            }
        }

    /**
     * Get feedback for a specific quiz document from Firestore using direct deserialization
     */
    suspend fun getFeedbackForQuiz(quizDocumentId: String): RecommendationsFeedback? =
        withContext(Dispatchers.Default) {
            try {
                val uid = AuthRepository.getCurrentUser()?.uid ?: return@withContext null
                val quizDoc = firestore.collection("users").document(uid)
                    .collection("quizzes").document(quizDocumentId).get()

                if (!quizDoc.exists || !quizDoc.contains("feedback")) {
                    return@withContext null
                }

                // Use direct deserialization instead of manual field extraction
                quizDoc.get<RecommendationsFeedback>("feedback")
            } catch (e: Exception) {
                Logger.error("Error getting feedback for quiz $quizDocumentId: ${e.message}")
                null
            }
        }

    /**
     * Set locked recommendation ranks for a specific quiz document
     * @param quizDocumentId The quiz document ID
     * @param ranks The list of locked recommendation ranks to store
     * @return true if successful, false otherwise
     */
    suspend fun setLockedRecommendationRanks(quizDocumentId: String, ranks: List<Int>): Boolean =
        withContext(Dispatchers.Default) {
            try {
                val uid = AuthRepository.getCurrentUser()?.uid ?: return@withContext false
                val quizDocRef = firestore.collection("users").document(uid)
                    .collection("quizzes").document(quizDocumentId)

                quizDocRef.update("lockedRecommendationRanks" to ranks)

                // Update cached quiz data
                _userQuizzes.update { quizzes ->
                    quizzes.map { quiz ->
                        if (quiz.quizId == quizDocumentId) {
                            quiz.copy(lockedRecommendationRanks = ranks)
                        } else quiz
                    }
                }

                true
            } catch (e: Exception) {
                Logger.error("Error setting locked recommendation ranks for quiz $quizDocumentId: ${e.message}")
                false
            }
        }

    /**
     * Updates saveQuizAnswersToUserDocument to return the quiz document ID for recommendations association
     */
    suspend fun saveQuizAnswersAndReturnDocumentId(quizUserAnswers: QuizUserAnswers): String? =
        withContext(Dispatchers.Default) {
            try {
                val uid = AuthRepository.getCurrentUser()?.uid ?: return@withContext null
                val userDocRef = firestore.collection("users").document(uid)

                // Ensure user document exists
                run {
                    val academicYear: String? =
                        AcademicYearResolver.resolveAcademicYear(
                            quizUserAnswers,
                            QuizTemplateRepository.questions.value
                        )
                    val base = buildMap<String, Any> {
                        if (academicYear != null) put("academicYear", academicYear)
                    }
                    if (base.isNotEmpty()) {
                        userDocRef.set(base, merge = true)
                    }
                }

                // Get the quizzes collection reference
                val quizzesRef = userDocRef.collection("quizzes")

                // Create Quiz object
                val currentTime = getCurrentDateTime()
                val quizData = QuizUserAnswersModel(
                    templateId = quizUserAnswers.templateId,
                    createdAt = currentTime,
                    lastModifiedAt = currentTime,
                    isUnlocked = false
                )

                // Create a new quiz document in the user's quizzes subcollection
                val quizDocRef = quizzesRef.add(quizData)

                // Get the Answers subcollection reference
                val answersRef = quizDocRef.collection("answers")

                // Add each answer to the Answers subcollection
                quizUserAnswers.answers.forEach { answer ->
                    val firestoreAnswer = CompletedAnswerModel(
                        questionId = answer.questionId,
                        answersIds = answer.answerIds,
                        openAnswer = answer.openAnswer,
                        fiveScaleAnswer = answer.fiveScaleAnswer
                    )
                    answersRef.add(firestoreAnswer)
                }

                // Set the Quiz doc ID as a quizId field
                quizDocRef.set(mapOf("quizId" to quizDocRef.id), merge = true)

                quizDocRef.id
            } catch (e: Exception) {
                Logger.error("Error saving quiz answers and returning document ID: ${e.message}")
                null
            }
        }

    /**
     * Listen for successful payments for a specific quiz using GitLive Firebase Flow patterns
     * This follows the recommended approach from GitLive documentation
     */
    suspend fun waitForPaymentSuccess(uid: String, quizId: String): Pair<Boolean, PaymentSuccessInfo?> =
        withContext(Dispatchers.Default) {
            var paymentInfo: PaymentSuccessInfo? = null

            try {
                firestore
                    .collection("users")
                    .document(uid)
                    .collection("payments")
                    .snapshots()
                    .first { snapshot ->
                        Logger.log("Payment snapshot received with ${snapshot.documents.size} documents")

                        for (document in snapshot.documents) {
                            try {
                                val data = document.js.data()
                                if (data == null) {
                                    Logger.warn("Payment document ${document.id} has no data")
                                    continue
                                }

                                val dynamicData = data.asDynamic()
                                val status = dynamicData.status as? String
                                val metadata = dynamicData.metadata
                                val paymentId = dynamicData.id as? String
                                val paymentQuizId = metadata?.quizId as? String
                                val amountMinorUnits = when (val rawAmount = dynamicData.amount) {
                                    is Number -> rawAmount.toLong()
                                    is String -> rawAmount.toDoubleOrNull()?.toLong()
                                    else -> null
                                }
                                val currencyCode = (dynamicData.currency as? String)

                                if (status == "succeeded" && paymentQuizId == quizId) {
                                    Logger.log("✅ Found successful payment for quiz $quizId, unlocking...")

                                    val unlockSuccess = setQuizUnlocked(quizId, true)
                                    if (unlockSuccess) {
                                        Logger.log("✅ Quiz $quizId unlocked successfully")
                                        paymentInfo = PaymentSuccessInfo(
                                            paymentId = paymentId,
                                            amountMinorUnits = amountMinorUnits,
                                            currencyCode = currencyCode
                                        )
                                        return@first true
                                    } else {
                                        Logger.error("❌ Failed to unlock quiz $quizId")
                                        return@first false
                                    }
                                }
                            } catch (docError: Exception) {
                                Logger.warn("Error processing payment document ${document.id}: ${docError.message}")
                            }
                        }
                        false // Continue listening if no matching payment found
                    }

                true to paymentInfo
            } catch (e: Exception) {
                Logger.error("Error in payment listener: ${e.message}")
                false to null
            }
        }

    /**
     * Gets a specific completed quiz with its answers, from local memory if it was already loaded
     * for the current user, otherwise from Firestore (and keeps it in local memory)
     */
    suspend fun getCompletedQuizWithAnswers(quizId: String): CompletedQuiz? = withContext(Dispatchers.Default) {
        try {
            val uid = AuthRepository.getCurrentUser()?.uid ?: return@withContext null

            // Try to get the quiz from local memory, but only if it was loaded for the current user
            if (userQuizzesOwnerUid == uid) {
                userQuizzes.value.firstOrNull { it.quizId == quizId }?.let {
                    if (it.answers.isNotEmpty()) {
                        return@withContext it
                    }
                }
            }

            // If not in the local memory, try to get the quiz from Firestore
            val quizDoc = firestore.collection("users").document(uid)
                .collection("quizzes").document(quizId).get()

            if (!quizDoc.exists) {
                Logger.warn("Quiz document $quizId does not exist for user $uid")
                return@withContext null
            }

            // Fetch answers for this quiz
            val answersSnapshot = quizDoc.reference.collection("answers").get()
            val answers = answersSnapshot.documents.map { answerDoc ->
                val answerData = answerDoc.data<CompletedAnswerModel>()

                UserAnswer(
                    questionId = answerData.questionId,
                    answerIds = answerData.answersIds,
                    openAnswer = answerData.openAnswer,
                    fiveScaleAnswer = answerData.fiveScaleAnswer
                )
            }

            Logger.debug("Fetched quiz $quizId with ${answers.size} answers")
            return@withContext quizDoc.toCompletedQuiz(answers).also { cacheQuiz(uid, it) }
        } catch (e: Exception) {
            Logger.error("Error fetching quiz $quizId with answers: ${e.message}")
            null
        }
    }

    /**
     * Keeps a quiz in local memory for [uid], dropping the quizzes cached for any other user
     */
    private fun cacheQuiz(uid: String, quiz: CompletedQuiz) {
        val sameOwner = userQuizzesOwnerUid == uid
        userQuizzesOwnerUid = uid
        _userQuizzes.update { cached ->
            (if (sameOwner) cached.filterNot { it.quizId == quiz.quizId } else emptyList()) + quiz
        }
    }

    /**
     * Drops the quizzes kept in local memory, so nothing of the current user stays around after logout
     */
    fun clearCachedQuizzes() {
        userQuizzesOwnerUid = null
        _userQuizzes.value = emptyList()
    }

    /**
     * Maps a quiz document to a [CompletedQuiz] with the given answers
     */
    private fun DocumentSnapshot.toCompletedQuiz(answers: List<UserAnswer>): CompletedQuiz {
        // Check for recommendations in this quiz document (cloud function format only)
        val recommendations = if (contains("recommendations")) {
            get<RecommendationsModel>("recommendations").toDomainModel()
        } else null

        return data<QuizUserAnswersModel>().toDomainModel(
            documentId = id,
            answers = answers,
            recommendations = recommendations
        )
    }

    /**
     * Updates user answers and persists to local storage
     */
    fun updateUserAnswers(answers: QuizUserAnswers) {
        _userAnswers.value = answers
        persistUserAnswers()
    }

    /**
     * Persists current user answers to local storage
     */
    private fun persistUserAnswers() {
        LocalStorage.save(KEY_QUIZ_USER_ANSWERS, Json.encodeToString(_userAnswers.value))
    }

    /**
     * Restores user answers from local storage
     */
    private fun restoreUserAnswers() {
        LocalStorage.get(KEY_QUIZ_USER_ANSWERS)?.let {
            _userAnswers.value = try {
                Json.decodeFromString<QuizUserAnswers>(it)
            } catch (_: Exception) {
                QuizUserAnswers(templateId = CURRENT_TEMPLATE_VERSION, answers = emptyList())
            }
        }
    }

    /**
     * Clears user answers and the quiz document ID (that is used for saving to remote)
     * from both memory and local storage
     */
    fun clearQuizUserData() {
        _savedQuizDocumentId.value = null
        currentAnswersHash = null
        _userAnswers.value = QuizUserAnswers(templateId = CURRENT_TEMPLATE_VERSION, answers = emptyList())
        LocalStorage.remove(KEY_QUIZ_USER_ANSWERS)
    }
}

// Utility function to get current date time as a string
private fun getCurrentDateTime(): String = Date().toISOString()
